// 🔧 vote_behavior_helpers.js
// ✅ Purpose: Central behavior rules for each Let'sGoVote voting type
// ✅ Features:
// - Keeps voting-type decisions out of page components
// - Uses event-level votingType from events/{eventId}.votingType
// - Supports current "standard" voting behavior safely
// - Prepares clean behavior paths for points, scorecard, and ranked choice voting
// - Provides helper checks for UI rendering, vote submission, ballot display, and leaderboard aggregation
// - Defaults older or incomplete events back to standard voting
// - Keeps future voting type expansion centralized and easier to maintain

import { serverTimestamp } from "firebase/firestore";
import { getEventVotingType } from "./event_voting_type_helpers";
import { getVotingTypeConfig } from "./voting_types";

// ✅ Leaderboard aggregation modes
// ✅ These tell leaderboard-style pages how results should be calculated
export const LEADERBOARD_AGGREGATION_MODES = {
  VOTE_COUNT: "vote_count",
  POINTS_TOTAL: "points_total",
  SCORE_AVERAGE: "score_average",
  RANKED_CHOICE: "ranked_choice",
};

// ✅ Vote UI modes
// ✅ These tell public voting pages what kind of voting interface to show
export const VOTE_UI_MODES = {
  SINGLE_ENTRY: "single_entry",
  POINTS_ENTRY: "points_entry",
  SCORECARD_ENTRY: "scorecard_entry",
  RANKED_LIST: "ranked_list",
};

// ✅ Safely gets the full voting type config for an event
export const getEventVotingTypeConfig = (eventDoc) => {
  const votingType = getEventVotingType(eventDoc);
  return getVotingTypeConfig(votingType);
};

// ✅ Checks if the event uses standard voting
export const usesStandardVoting = (eventDoc) => {
  return getEventVotingType(eventDoc) === "standard";
};

// ✅ Checks if the event uses points voting
export const usesPointsVoting = (eventDoc) => {
  return getEventVotingType(eventDoc) === "points";
};

// ✅ Checks if the event uses scorecard voting
export const usesScorecardVoting = (eventDoc) => {
  return getEventVotingType(eventDoc) === "scorecard";
};

// ✅ Checks if the event uses ranked choice voting
export const usesRankedChoiceVoting = (eventDoc) => {
  return getEventVotingType(eventDoc) === "ranked_choice";
};

// ✅ Returns true when the voter is choosing one entry at a time
export const allowsSingleEntryVote = (eventDoc) => {
  const config = getEventVotingTypeConfig(eventDoc);
  return config.requiresSingleEntry === true;
};

// ✅ Returns true when the voting type supports points
export const allowsPoints = (eventDoc) => {
  const config = getEventVotingTypeConfig(eventDoc);
  return config.supportsPoints === true;
};

// ✅ Returns true when the voting type supports ranking
export const allowsRanking = (eventDoc) => {
  const config = getEventVotingTypeConfig(eventDoc);
  return config.supportsRanking === true;
};

// ✅ Returns true when the voting type supports scorecard fields
export const allowsScoreFields = (eventDoc) => {
  const config = getEventVotingTypeConfig(eventDoc);
  return config.supportsScoreFields === true;
};

// ✅ Returns the UI mode that vote.js should render
export const getVoteUiMode = (eventDoc) => {
  if (usesRankedChoiceVoting(eventDoc)) {
    return VOTE_UI_MODES.RANKED_LIST;
  }

  if (usesScorecardVoting(eventDoc)) {
    return VOTE_UI_MODES.SCORECARD_ENTRY;
  }

  if (usesPointsVoting(eventDoc)) {
    return VOTE_UI_MODES.POINTS_ENTRY;
  }

  return VOTE_UI_MODES.SINGLE_ENTRY;
};

// ✅ Returns the leaderboard calculation mode for the event
export const getLeaderboardAggregationMode = (eventDoc) => {
  if (usesRankedChoiceVoting(eventDoc)) {
    return LEADERBOARD_AGGREGATION_MODES.RANKED_CHOICE;
  }

  if (usesScorecardVoting(eventDoc)) {
    return LEADERBOARD_AGGREGATION_MODES.SCORE_AVERAGE;
  }

  if (usesPointsVoting(eventDoc)) {
    return LEADERBOARD_AGGREGATION_MODES.POINTS_TOTAL;
  }

  return LEADERBOARD_AGGREGATION_MODES.VOTE_COUNT;
};

// ✅ Builds the base vote document shared by all voting types
const buildBaseVoteDocument = ({ uid, catid, eventId }) => {
  return {
    uid,
    catid,
    eventId,
    createdAt: serverTimestamp(),
  };
};

// ✅ Builds a standard vote document
// ✅ Current production format: one voter selects one car in one category
export const buildStandardVoteDocument = ({
  uid,
  cid,
  catid,
  eventId,
  bestInShow = false,
}) => {
  return {
    ...buildBaseVoteDocument({ uid, catid, eventId }),
    cid,
    votingType: "standard",
    bestInShow,
  };
};

// ✅ Builds a points vote document
// ⚠️ Future behavior placeholder — do not wire into production submit flow yet
export const buildPointsVoteDocument = ({
  uid,
  cid,
  catid,
  eventId,
  points = 0,
}) => {
  return {
    ...buildBaseVoteDocument({ uid, catid, eventId }),
    cid,
    votingType: "points",
    points,
  };
};

// ✅ Builds a scorecard vote document
// ⚠️ Future behavior placeholder — do not wire into production submit flow yet
export const buildScorecardVoteDocument = ({
  uid,
  cid,
  catid,
  eventId,
  scores = {},
  totalScore = 0,
}) => {
  return {
    ...buildBaseVoteDocument({ uid, catid, eventId }),
    cid,
    votingType: "scorecard",
    scores,
    totalScore,
  };
};

// ✅ Builds a ranked choice vote document
// ⚠️ Future behavior placeholder — do not wire into production submit flow yet
export const buildRankedChoiceVoteDocument = ({
  uid,
  catid,
  eventId,
  rankings = [],
}) => {
  return {
    ...buildBaseVoteDocument({ uid, catid, eventId }),
    votingType: "ranked_choice",
    rankings,
  };
};

// ✅ Main vote document builder
// ✅ Pages can call this without needing to know the exact Firestore shape for each voting type
export const buildVoteDocumentForEvent = ({
  eventDoc,
  uid,
  cid,
  catid,
  eventId,
  bestInShow = false,
  points = 0,
  scores = {},
  totalScore = 0,
  rankings = [],
}) => {
  const votingType = getEventVotingType(eventDoc);

  if (votingType === "points") {
    return buildPointsVoteDocument({
      uid,
      cid,
      catid,
      eventId,
      points,
    });
  }

  if (votingType === "scorecard") {
    return buildScorecardVoteDocument({
      uid,
      cid,
      catid,
      eventId,
      scores,
      totalScore,
    });
  }

  if (votingType === "ranked_choice") {
    return buildRankedChoiceVoteDocument({
      uid,
      catid,
      eventId,
      rankings,
    });
  }

  return buildStandardVoteDocument({
    uid,
    cid,
    catid,
    eventId,
    bestInShow,
  });
};